/**
 * PDF Generator for Reports
 * Uses jsPDF + svg2pdf.js for vector-quality PDF generation
 */

import { jsPDF } from 'jspdf';
import { svg2pdf } from 'svg2pdf.js';
import {
  PDF_COLORS,
  PDF_PAGE,
  PDF_HEADER,
  PDF_FOOTER,
  PDF_TYPOGRAPHY,
  PDF_CHARTS,
  hexToRgb,
} from './pdf-styles';
import { registerMontserratFonts } from './fonts';
import {
  parseReportContent,
  parseMarkdownSections,
  stripMarkdownFormatting,
  parseTextWithLinks,
  parseTrendArrow,
  type MarkdownSection,
  type ListItem,
  type ScorecardBlock,
} from './markdown-parser';
import { parseSVGString, getSVGDimensions, scaleSVGToFit } from './svg-utils';
import type { Report, ReportType } from '@/lib/types';

// Glitch logo as inline SVG (from /public/logo_lightbg.svg)
const LOGO_SVG = `<svg width="94" height="32" viewBox="0 0 94 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M93.4884 21.9572C93.3152 21.74 93.1492 21.591 93.0044 21.4589C92.6907 21.1736 92.4636 20.9678 92.3798 20.2794C92.2762 19.4163 92.2861 18.3574 92.2975 17.2374C92.3174 15.0982 92.3415 12.6738 91.5097 11.2131C90.9319 10.1996 88.956 8.28474 87.8942 7.25561L87.6629 7.03134C86.2519 5.6587 84.1113 5.23427 82.0573 5.90853C82.0417 5.64308 82.063 5.3748 82.0829 5.11362C82.117 4.68068 82.1524 4.23212 82.0204 3.78782L82.0148 3.76795L82.0006 3.75233C81.5123 3.22855 80.9856 2.72179 80.4775 2.23207C79.9537 1.72674 79.4114 1.20579 78.9118 0.666385C78.828 0.576958 78.7741 0.493208 78.7201 0.413717C78.5711 0.189439 78.4547 0.0120038 77.8968 0.000647943C77.3063 -0.0121274 76.3056 0.166727 75.5746 0.297319L75.2949 0.347002C73.9109 0.586894 70.6334 1.28954 70.0812 2.61249C69.6937 3.54083 70.341 4.08875 70.9669 4.61822C71.2551 4.86237 71.5518 5.11362 71.7406 5.39751C71.8655 7.19032 71.9237 9.047 71.9123 10.9207C71.9038 10.9136 71.8939 10.9023 71.8811 10.8881C71.8612 10.8668 71.8399 10.8441 71.8144 10.8242C71.1543 10.3174 70.5709 9.66021 69.9534 8.96325C69.0762 7.97245 68.1677 6.94759 67.0236 6.40677C64.1832 5.06394 60.2172 5.41313 57.5926 7.23432C57.3655 7.39188 57.1568 7.57642 56.9538 7.75527C56.7466 7.93697 56.5337 8.12576 56.3037 8.2819C56.0226 7.99658 55.7288 7.71552 55.4435 7.44156C55.0276 7.04269 54.5989 6.63104 54.2043 6.2052C54.1475 6.14416 54.1163 6.07035 54.0836 5.99795C54.0552 5.93266 54.0283 5.87162 53.9857 5.82762C53.7756 5.61043 53.3526 5.61611 53.0446 5.61895H52.9906L49.3298 1.99644L47.2091 2.04328L41.6021 5.5494C41.6121 5.13491 41.6192 4.74313 41.3452 4.35278C40.9279 3.75659 40.2877 3.1888 39.6106 2.58836C38.9307 1.98366 38.2266 1.35909 37.7823 0.713226L37.7653 0.694772C36.1031 -0.672188 31.6729 0.240541 30.2477 1.86017L29.8928 2.47906C29.6487 2.22923 29.396 1.98224 29.1505 1.74235C28.8453 1.44426 28.5287 1.13623 28.2306 0.822524C28.171 0.760067 28.1199 0.67206 28.0702 0.588311C28.0092 0.486108 27.9467 0.37965 27.863 0.307256C27.6671 0.138338 27.1646 -0.000771537 26.9034 0.000647943C25.1291 0.0233596 20.8891 0.846658 19.5207 1.96095C18.7542 2.58552 18.5271 3.28958 18.8464 4.0561C18.9856 4.38826 19.4015 4.74029 19.8401 5.11362C20.2134 5.43016 20.5995 5.75806 20.7556 6.03912C20.8948 6.29036 20.9288 6.62536 20.9629 6.95043C20.9813 7.12928 20.9998 7.31239 21.0367 7.48273C21.0183 7.46286 20.9984 7.44156 20.9742 7.42311C20.4235 6.98875 19.2042 5.75238 18.7286 5.18175C18.6264 5.05968 18.5611 4.94328 18.4987 4.83256C18.3425 4.55576 18.2063 4.31587 17.63 4.21935C16.8038 4.08024 16.2048 4.29174 15.5703 4.51602C15.2793 4.61822 14.9784 4.72468 14.649 4.79707C13.8485 4.97309 12.8278 4.74313 11.8413 4.51885C11.2295 4.37974 10.5964 4.2378 10.0371 4.19947C7.87954 4.05468 5.76593 4.43937 4.08385 5.28396C2.13206 6.26198 0.82046 7.82483 0.288154 9.80358C0.235634 9.99947 0.201567 10.201 0.168919 10.3969C0.114978 10.7191 0.0837494 10.9605 0.0156144 11.3295L0 12.9449C0 12.9449 0.00567662 13.2586 0.0496805 13.4417C0.0709727 13.5283 0.095105 13.6475 0.110719 13.737C0.403132 15.3594 1.04758 16.8158 1.92766 17.8378C2.30524 18.2765 2.75947 18.6328 3.19951 18.9791C3.6651 19.3453 4.1463 19.7229 4.52104 20.1871C4.47846 20.197 4.39471 20.1956 4.34645 20.1956C4.27973 20.1956 4.2187 20.1956 4.17185 20.207C3.13137 20.4625 2.27401 21.212 1.81835 22.2652C1.35702 23.3312 1.40103 24.5335 1.93617 25.4832C2.31091 26.1475 2.7396 26.4725 3.19383 26.8161C3.40108 26.9736 3.61684 27.1354 3.83827 27.3399C4.23857 27.7061 4.61899 28.1021 4.98663 28.4854C5.76735 29.2973 6.57503 30.1376 7.5999 30.7296C9.06764 31.577 10.6319 32 12.1962 32C13.8939 32 15.5902 31.5018 17.1587 30.5095C19.7847 28.8488 21.4824 25.3625 21.3348 21.9756C21.5804 22.1942 21.8046 22.427 22.0232 22.6527C22.6109 23.2603 23.1645 23.8337 24.0602 24.1162C25.6685 24.623 30.8141 24.7692 32.2392 23.8139C32.5742 23.5896 32.7559 23.2787 32.8851 22.9934C32.9873 23.0601 33.1789 23.2574 33.301 23.3838C33.4444 23.5328 33.5792 23.6719 33.6743 23.7358C35.179 24.7351 40.3374 24.6144 41.9144 24.0978C42.5915 23.8763 43.3566 23.4093 43.4759 22.7634C43.6448 21.8564 43.1792 21.476 42.7292 21.1069C42.4879 20.9096 42.2395 20.7052 42.0706 20.4156C41.9499 20.2084 41.754 19.3709 41.754 19.1253V10.7518C42.0067 10.9008 42.3956 11.304 42.4354 11.4729C42.5092 12.3232 42.4765 13.1862 42.4453 14.0194C42.363 16.2069 42.2849 18.2722 44.0195 20.0196C44.2651 20.2666 44.5036 20.5107 44.7364 20.7506C47.3979 23.4746 49.5001 25.628 54.0311 24.0978C55.1199 23.7301 57.0674 22.745 57.4691 21.3851C57.6409 20.8031 57.5032 20.224 57.0589 19.6647C56.6132 19.104 56.034 18.5746 55.4747 18.0621C54.853 17.4943 54.2114 16.9067 53.7245 16.2537C53.5102 16.0507 53.2476 16.0607 52.9935 16.0692C52.9708 16.0692 52.9481 16.0706 52.9253 16.072C52.9452 15.6376 52.931 15.1863 52.9168 14.7476C52.8998 14.2309 52.8813 13.6972 52.9225 13.2004C52.9552 12.7987 53.0645 12.3686 53.2703 12.3686H54.3264L54.2526 13.7512C54.3831 15.693 55.0744 17.4077 56.2512 18.7122C57.357 19.9373 60.1051 22.6754 61.4479 23.449C63.0306 24.3604 65.0846 24.7465 67.0832 24.508C69.1756 24.2582 70.9343 23.3625 72.0486 21.9813C72.2885 22.1971 72.4986 22.4029 72.703 22.6016C73.5816 23.4547 74.2176 24.0722 75.7563 24.305C77.1928 24.5236 81.7039 24.7521 82.7543 23.5129C83.43 22.7166 83.007 22.224 82.5967 21.7485C82.4704 21.6009 82.3412 21.4504 82.239 21.2886L84.655 23.6677C86.1384 24.7692 91.01 24.6301 92.5587 24.058C93.2088 23.8167 93.6502 23.432 93.7709 22.9991C93.8646 22.6627 93.7681 22.3135 93.4856 21.96L93.4884 21.9572ZM53.9502 17.9329C53.6947 18.9166 52.535 19.6605 52.1844 19.8649C49.9828 21.1537 46.5008 21.598 44.3815 19.8862C42.4652 18.3375 42.6071 15.2828 42.732 12.5872C42.7888 11.3551 42.8442 10.1897 42.7122 9.21592C42.7022 9.14636 42.6142 8.78865 42.424 8.78865H40.1443C39.9683 8.69781 39.9797 7.58493 40.0535 7.01714L40.221 6.84396L47.3752 2.33427C47.4987 2.33995 47.6917 2.33002 47.9131 2.32008C48.3518 2.29879 49.1694 2.26046 49.2943 2.39957C49.4803 2.60823 49.449 3.50109 49.4235 4.21935C49.4079 4.6821 49.3923 5.11929 49.432 5.36203C49.4646 5.56643 49.5697 5.73677 49.7116 5.8191C50.0679 6.02492 51.0743 5.98944 52.0467 5.95537C52.6031 5.9355 53.1269 5.91704 53.4236 5.95253C53.7344 5.98944 53.8253 6.07886 53.8622 6.39115C53.9275 6.94901 53.9034 8.41817 53.6166 8.65238C53.3867 8.84259 52.2483 8.81278 51.4179 8.79149C50.8926 8.77872 50.4398 8.76736 50.1843 8.79717C49.6989 8.85679 49.4732 8.97744 49.4221 9.5353C49.2971 10.9207 49.3213 13.4062 49.4221 14.8584C49.4874 15.7853 49.8011 16.478 50.3092 16.8101C50.745 17.0955 51.3043 17.121 51.9714 16.884C52.1489 16.8215 52.3206 16.725 52.4867 16.6341C52.9892 16.3545 53.3867 16.133 53.7813 16.8045C53.9942 17.165 54.051 17.544 53.9502 17.9315V17.9329ZM31.3975 11.145C31.6516 11.7539 31.5692 15.1806 31.45 16.1047L31.4387 16.1983C31.3961 16.5404 31.3393 16.9847 31.1491 17.2743V11.0698C31.1519 11.0513 31.1789 10.9818 31.2044 10.9221C31.2087 10.9264 31.213 10.9307 31.2186 10.9349C31.2925 11.003 31.3677 11.074 31.3989 11.1464L31.3975 11.145ZM30.7545 18.1785C31.0157 17.9699 31.2626 17.7725 31.4089 17.5454C31.9539 16.7023 31.9184 13.0585 31.8617 12.1557C31.8347 11.7483 31.7367 10.881 31.5522 10.5218C31.4202 10.2663 31.1008 10.0662 30.7616 9.85468C30.34 9.59066 29.9042 9.31812 29.8488 8.94622C29.8162 8.72052 29.9241 8.47069 30.1782 8.18253C31.1576 7.07108 35.4529 6.17823 36.9931 6.05899C37.9541 5.98376 38.1855 6.00505 38.2649 7.0214C38.3742 8.42385 38.3118 9.95404 38.2493 11.4331C38.1812 13.0613 38.1116 14.7462 38.272 16.275C38.3146 16.6895 38.485 17.2786 38.7674 17.659C38.9364 17.8861 39.155 18.0309 39.365 18.17C39.7114 18.3985 40.0095 18.5958 39.9953 19.114C39.957 20.5632 37.4161 20.8443 36.6382 20.8968C34.9462 21.0104 30.9816 20.9337 30.2222 19.977C29.5564 19.1381 30.1384 18.6711 30.7545 18.1785ZM31.5295 5.35209C31.6558 5.47416 31.8006 5.6005 31.9525 5.73251C32.2534 5.99512 32.5643 6.26623 32.7332 6.51039C32.7488 6.53168 32.7645 6.55013 32.7801 6.56575C32.7914 6.57852 32.7985 6.58562 32.8028 6.59272L31.3961 7.0143L31.5295 5.35209ZM37.3423 3.46844C36.4083 4.24206 33.7652 4.95889 32.0107 4.69203C31.2683 4.57989 30.7559 4.31161 30.4862 3.89712C30.2193 3.48689 30.1285 3.08092 30.2179 2.6934C30.3258 2.2193 30.6977 1.77926 31.2925 1.41871C32.208 0.863691 33.9455 0.362614 35.4416 0.362614C36.3728 0.362614 37.2117 0.557084 37.7028 1.05106C37.9825 1.33354 38.1202 1.68415 38.1017 2.06883C38.0776 2.57132 37.7937 3.09369 37.3423 3.46702V3.46844ZM56.9808 8.17402C58.6331 6.6793 60.846 5.88724 63.0746 5.88724C64.2769 5.88724 65.4835 6.11719 66.6106 6.59272C67.7916 7.08954 69.1528 8.2095 69.5318 9.58924C69.7604 10.4168 69.6028 11.206 69.0663 11.9371C68.6305 12.529 67.81 12.9534 66.9739 13.0173C66.3153 13.0684 65.7418 12.8867 65.3586 12.5063C65.0378 12.1869 64.8447 11.6588 64.6573 11.1478C64.284 10.1272 63.8624 8.97177 62.2968 9.44871C61.6665 9.64034 61.218 10.0633 60.9624 10.7078C60.4983 11.8803 60.8205 13.5127 61.3599 14.4538C62.4004 16.2693 64.1634 16.9478 66.321 16.3602C66.5566 16.2963 66.788 16.1955 67.0123 16.0976C67.7064 15.7952 68.3054 15.5354 68.8264 16.4113C69.0762 16.8314 69.1259 17.2828 68.9754 17.7527C68.6376 18.8116 67.3473 19.7953 66.2926 20.2609C63.9632 21.29 61.2109 21.2843 58.9312 20.2439C56.9765 19.3524 55.5656 17.7725 54.958 15.7966C54.0723 12.9165 54.809 10.1386 56.9794 8.17544L56.9808 8.17402ZM78.503 3.47412C78.4448 4.14979 78.3894 4.78856 78.405 5.36628C78.4433 5.58914 78.4263 5.95395 78.4079 6.34005C78.3695 7.14915 78.3269 8.06472 78.7287 8.37984C78.9118 8.52321 79.1006 8.58283 79.2908 8.55728C79.7095 8.50192 80.0388 8.05904 80.3298 7.66868C80.432 7.53241 80.5271 7.40324 80.6151 7.31097C81.684 6.17681 82.8068 5.79923 84.6138 5.96531C88.8666 6.35708 88.8084 10.475 88.7573 14.1074C88.7459 14.9449 88.7346 15.737 88.7786 16.4425C88.8297 17.2431 88.9801 17.8123 89.2385 18.1799C89.362 18.356 89.5394 18.4936 89.7112 18.6271C89.9738 18.8301 90.2208 19.0217 90.252 19.3269C90.3216 20.0125 89.5849 20.3745 89.1406 20.5292C87.6969 21.0303 83.0212 21.1935 81.6869 20.2666C81.4285 20.0877 81.2866 19.9074 81.2553 19.7158C81.2057 19.4135 81.4313 19.1083 81.6712 18.7846C81.7862 18.6299 81.904 18.4695 81.992 18.3077C82.6805 17.0273 82.6337 13.4147 82.3469 12.0208C82.0659 10.6581 81.3391 9.95121 80.2319 9.95121C80.0729 9.95121 79.9054 9.9654 79.7294 9.99521C78.4575 10.2096 78.3766 11.6234 78.3284 12.4679C78.2631 13.6191 78.1836 17.1153 78.6676 18.0465C78.7925 18.2864 78.9671 18.4539 79.1219 18.6029C79.4058 18.8755 79.63 19.0898 79.5051 19.6434C79.4001 20.1062 78.6548 20.4639 78.2758 20.5888C76.6633 21.1168 72.385 21.0473 70.9627 20.2907C70.5439 20.0678 70.26 19.8194 70.2373 19.3326C70.216 18.8797 70.4985 18.6597 70.8264 18.4056C70.9826 18.2836 71.1444 18.1586 71.2806 17.9982C72.0103 17.148 72.0997 15.6078 72.1636 14.4822L72.1735 14.3076C72.2899 12.3317 72.2246 10.3075 72.1664 8.52321C72.1565 8.22938 72.1607 7.89438 72.1636 7.53951C72.1707 6.77867 72.1792 5.91562 72.0543 5.29531C71.9393 4.72752 71.4524 4.38542 71.0223 4.08449C70.6618 3.83183 70.3211 3.59193 70.2686 3.27113C70.2345 3.05963 70.3225 2.8169 70.5368 2.52732C71.495 1.23276 75.8727 0.466237 77.5363 0.364035L77.6016 0.359776C78.1807 0.32287 78.4874 0.328549 78.5626 0.866532C78.6676 1.6146 78.5853 2.56139 78.5072 3.47838L78.503 3.47412ZM64.4302 14.3133C64.6545 13.7213 65.0449 13.27 65.5303 13.0372L67.9491 15.4701C67.6198 15.456 67.3302 15.5979 67.0478 15.7356C66.9044 15.8051 66.7695 15.8704 66.6304 15.9159L66.6091 15.923C66.0073 16.1245 65.651 16.2267 65.5474 16.2267H64.416C64.1804 15.6703 64.1847 14.9591 64.4302 14.3133ZM70.8704 17.95L69.3061 16.4567C69.4183 16.468 69.5262 16.4851 69.6326 16.5007C69.891 16.5404 70.1564 16.5802 70.463 16.5575C70.852 16.5277 71.177 16.3999 71.5205 16.2636C71.5702 16.2438 71.6199 16.2239 71.671 16.2054C71.6199 16.6072 71.3048 17.5341 70.9925 17.862C70.9471 17.9088 70.9059 17.9386 70.8718 17.95H70.8704ZM82.2021 14.1727C82.1993 14.3303 82.1993 14.4921 82.1979 14.6568C82.1908 15.6206 82.1837 16.7094 81.9225 17.5923C81.9282 17.2644 81.914 16.9265 81.8998 16.5958C81.8785 16.0905 81.8558 15.5667 81.9055 15.0982C81.9324 14.847 82.0318 14.4325 82.2021 14.1727ZM10.859 23.1524C10.8491 23.1723 10.8321 23.1964 10.8093 23.2276C10.5836 23.5186 10.0244 23.9004 9.84268 23.9629L9.04635 23.124C9.64963 23.2092 10.2671 23.283 10.8604 23.1524H10.859ZM7.40259 21.9969C7.5346 22.2638 7.63681 22.5874 7.73617 22.9011C7.94767 23.5711 8.16485 24.2624 8.69716 24.4839C9.035 24.6244 9.45658 24.5619 9.98463 24.2894C10.2146 24.1716 10.7398 23.8011 10.9059 23.6251C11.8725 22.603 11.9165 20.8727 11.4651 19.7627C11.248 19.2289 10.974 18.9067 10.6262 18.7761C10.0741 18.5689 9.43103 18.8939 8.8093 19.2091C8.43597 19.3979 8.04988 19.5937 7.68791 19.6732C6.18752 20.0054 4.707 19.6775 3.40959 18.725C1.74454 17.5043 0.546501 15.3239 0.35771 13.1692C0.127754 10.5516 0.853108 8.35429 2.45712 6.81416C4.35497 4.99012 7.36994 4.19095 10.7284 4.62106C11.1358 4.67358 11.5602 4.76868 12.0088 4.86805C12.7909 5.04264 13.6 5.22292 14.3254 5.16472C14.7782 5.12781 15.1998 4.97735 15.6058 4.83114C16.0742 4.66364 16.5597 4.48905 17.0863 4.49472C17.9621 4.50324 18.3766 4.82688 18.3894 5.51533C18.3993 6.05757 18.3056 6.7077 18.2162 7.33795C18.1566 7.75385 18.0998 8.14563 18.0714 8.51043C17.7889 12.0634 17.8471 15.04 17.9153 18.4865C17.9664 21.0714 17.1587 23.5158 15.6413 25.371C14.0557 27.3086 11.7859 28.4456 9.24792 28.5734C7.05056 28.6798 3.79427 27.5244 2.45144 25.6209C1.75164 24.6301 1.63382 23.5513 2.10225 22.4171C2.5792 21.2616 3.66794 20.4894 4.87734 20.4483C4.90998 20.4483 4.94263 20.4469 4.97528 20.4469C6.0186 20.4469 6.92139 21.0203 7.40401 21.9941L7.40259 21.9969ZM27.7196 0.645089C28.0418 0.97157 28.012 1.59046 27.978 2.30872C27.9737 2.39673 27.9694 2.48758 27.9652 2.57984C27.9368 3.24416 27.8729 3.92551 27.809 4.58557C27.7409 5.30241 27.6699 6.0448 27.6458 6.77299C27.5876 8.56863 27.6146 10.3998 27.6415 12.1713C27.6657 13.8037 27.6912 15.49 27.6501 17.1565C27.7409 17.967 28.2505 18.214 28.6991 18.4326C29.0809 18.6186 29.3818 18.7648 29.3946 19.2091C29.4173 19.95 28.77 20.3375 28.4904 20.4696C27.1334 21.1112 22.6436 21.0146 21.2127 20.6725C20.9075 20.6002 19.8869 20.3134 19.5292 19.6803C19.3915 19.4362 19.3674 19.1622 19.4611 18.8698C19.5193 18.6867 19.7379 18.5802 19.992 18.4582C20.3837 18.2679 20.872 18.0323 21.0665 17.3864C21.322 16.5319 21.3816 14.9279 21.4129 14.0663V14.0294C21.4824 12.1542 21.6428 7.76379 21.129 6.08028C20.8735 5.24279 20.503 5.00858 20.0346 4.71191C19.8699 4.60686 19.6825 4.48904 19.4824 4.33148C19.2013 4.11004 19.0452 3.81763 19.0296 3.48831C19.0111 3.09653 19.1914 2.68914 19.5235 2.37118C20.7556 1.19585 24.8551 0.426492 26.6153 0.359776C27.149 0.339904 27.511 0.433587 27.721 0.645089H27.7196Z" fill="#0f172a"/>
<path d="M8.77481 7.5459C8.15024 7.65236 7.67613 8.05407 7.36527 8.7411C6.54339 10.5566 7.08989 13.996 7.88196 15.2792C8.26806 15.9052 8.80746 16.2303 9.40222 16.2303C9.74432 16.2303 10.1049 16.1224 10.4654 15.9024C11.98 14.9755 11.8764 11.8271 11.4548 10.0598C11.0105 8.19318 10.085 7.32304 8.77481 7.5459ZM11.3554 11.4239C11.4619 12.4048 11.4221 13.3047 11.2362 14.0996C11.1368 14.5255 10.87 15.2565 10.4683 15.4794C10.3178 13.9705 10.3263 12.3437 11.354 11.4239H11.3554Z" fill="#0f172a"/>
</svg>`;

const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  WEEKLY_PERFORMANCE: 'Weekly Performance Report',
  MONTHLY_SUMMARY: 'Monthly Performance Report',
  QUARTERLY_REVIEW: 'Quarterly Review Report',
  STRATEGIC_6MONTH: 'Google Ads Report',
  COMPETITOR_REPORT: 'Competitor Analysis Report',
  INTELLIGENCE: 'Intelligence Report',
  CUSTOM_SHORT: 'Custom Performance Report',
  CUSTOM_LONG: 'Custom Performance Report',
};

interface GeneratePDFOptions {
  report: Report;
  chartSVGs: Map<number, string>;
  onProgress?: (stage: string, progress: number) => void;
}

/**
 * Generates a PDF from a report with embedded charts
 */
export async function generateReportPDF({
  report,
  chartSVGs,
  onProgress,
}: GeneratePDFOptions): Promise<Blob> {
  // Create PDF document
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'a4',
  });

  // Register Montserrat fonts for proper Unicode support
  registerMontserratFonts(doc);

  // Ensure consistent character spacing throughout the document
  doc.setCharSpace(0);

  let currentY: number = PDF_PAGE.margins.top;

  onProgress?.('Initializing document...', 10);

  // Add first page header
  await addHeader(doc, report);
  currentY = PDF_HEADER.lineY + 20;

  // Parse content into blocks
  const contentBlocks = parseReportContent(report.content);

  onProgress?.('Rendering content...', 20);

  // Track chart index for matching SVGs
  let chartIndex = 0;

  // Process each content block
  for (let i = 0; i < contentBlocks.length; i++) {
    const block = contentBlocks[i];
    const progress = 20 + (i / contentBlocks.length) * 70;
    onProgress?.('Rendering content...', progress);

    if (block.type === 'text') {
      currentY = await renderTextBlock(doc, block.content, currentY, (): number => {
        addNewPage(doc, report);
        return PDF_HEADER.lineY + 20;
      });
    } else if (block.type === 'scorecard') {
      currentY = renderScorecardBlock(doc, block as ScorecardBlock, currentY, (): number => {
        addNewPage(doc, report);
        return PDF_HEADER.lineY + 20;
      });
    } else if (block.type === 'chart') {
      const svgString = chartSVGs.get(chartIndex);
      if (svgString) {
        currentY = await renderChartBlock(doc, svgString, currentY, (): number => {
          addNewPage(doc, report);
          return PDF_HEADER.lineY + 20;
        });
      }
      chartIndex++;
    }

    // Yield to main thread periodically
    if (i % 3 === 0) {
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }

  // Add footers to all pages now that we know the actual total
  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);
    addFooter(doc, page, totalPages);
  }

  onProgress?.('Finalizing PDF...', 95);

  // Return as blob
  const blob = doc.output('blob');

  onProgress?.('Complete', 100);

  return blob;
}

/**
 * Branded PDF for a standalone markdown document (#6085 Phase 5 — the
 * strategy playbook). Same Montserrat/brand/header/footer chrome as the
 * report export; the whole document renders through the markdown stack
 * (headings, lists, tables), no charts or scorecards.
 */
export async function generateMarkdownPDF({
  content,
  headerLabel,
  createdAt,
  cover,
  onProgress,
}: {
  content: string;
  /** Small caption on continuation pages, e.g. "Strategy Report". */
  headerLabel: string;
  /** ISO date shown top-right; defaults to now. Unused with a cover. */
  createdAt?: string;
  /** With a cover, page 1 opens on the playbook's cobalt band (title and
   * date) instead of the report header, and the body renders in the
   * playbook theme so the file matches the screen (#6671). */
  cover?: { title: string; dateLabel: string | null };
  onProgress?: (message: string, progress: number) => void;
}): Promise<Blob> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  registerMontserratFonts(doc);
  doc.setCharSpace(0);

  onProgress?.('Initializing document...', 10);
  let startY = PDF_HEADER.lineY + 20;
  if (cover) {
    startY = drawCoverBand(doc, cover) + 28;
  } else {
    await addHeader(doc, { created_at: createdAt ?? new Date().toISOString() } as Report);
  }

  onProgress?.('Rendering content...', 30);
  await renderTextBlock(doc, content, startY, (): number => {
    doc.addPage();
    const margins = PDF_PAGE.margins;
    doc.setDrawColor(...hexToRgb(PDF_COLORS.primary));
    doc.setLineWidth(1.5);
    doc.line(margins.left, PDF_HEADER.lineY, PDF_PAGE.width - margins.right, PDF_HEADER.lineY);
    doc.setFont('Montserrat', 'normal');
    doc.setFontSize(PDF_TYPOGRAPHY.caption);
    doc.setTextColor(...hexToRgb(PDF_COLORS.textMuted));
    doc.text(headerLabel, margins.left, 35);
    return PDF_HEADER.lineY + 20;
  }, cover ? 'playbook' : 'report');

  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);
    addFooter(doc, page, totalPages);
  }
  onProgress?.('Finalizing PDF...', 95);
  const blob = doc.output('blob');
  onProgress?.('Complete', 100);
  return blob;
}

/**
 * The playbook's cover band (#6671): full-bleed cobalt with the small brand
 * label, the title and the date, as the screen renders it. Returns the
 * band's bottom edge.
 */
function drawCoverBand(doc: jsPDF, cover: { title: string; dateLabel: string | null }): number {
  const margins = PDF_PAGE.margins;
  const tint: [number, number, number] = [191, 182, 244]; // white at 70% over cobalt
  const titleSize = 22;
  const titleLine = titleSize * 1.2;
  doc.setFont('Montserrat', 'bold');
  doc.setFontSize(titleSize);
  const titleLines: string[] = doc.splitTextToSize(cover.title, PDF_PAGE.contentWidth);
  let y = 44;
  const bandHeight = y + 22 + titleLines.length * titleLine + (cover.dateLabel ? 18 : 0) + 24;
  doc.setFillColor(...hexToRgb(PDF_COLORS.primary));
  doc.rect(0, 0, PDF_PAGE.width, bandHeight, 'F');

  doc.setFont('Montserrat', 'normal');
  doc.setFontSize(8);
  doc.setCharSpace(1.5);
  doc.setTextColor(...tint);
  doc.text('GLITCHADS', margins.left, y);
  doc.setCharSpace(0);
  y += 26;

  doc.setFont('Montserrat', 'bold');
  doc.setFontSize(titleSize);
  doc.setTextColor(255, 255, 255);
  for (const line of titleLines) {
    doc.text(line, margins.left, y);
    y += titleLine;
  }
  if (cover.dateLabel) {
    doc.setFont('Montserrat', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...tint);
    doc.text(cover.dateLabel, margins.left, y - 4);
  }
  return bandHeight;
}

/**
 * Adds header with logo and report info
 */
async function addHeader(doc: jsPDF, report: Report): Promise<void> {
  const margins = PDF_PAGE.margins;

  // Add logo
  try {
    const logoSvg = parseSVGString(LOGO_SVG);
    const logoDims = getSVGDimensions(logoSvg);
    const scaledLogo = scaleSVGToFit(
      logoDims.width,
      logoDims.height,
      PDF_HEADER.logoMaxWidth,
      PDF_HEADER.logoMaxHeight
    );

    await svg2pdf(logoSvg, doc, {
      x: margins.left,
      y: 20,
      width: scaledLogo.width,
      height: scaledLogo.height,
    });
  } catch (error) {
    // Fallback: just write "Glitch" text
    doc.setFont('Montserrat', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...hexToRgb(PDF_COLORS.text));
    doc.text('Glitch', margins.left, 35);
  }

  // Add created date on right side
  const createdAt = new Date(report.created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  doc.setFont('Montserrat', 'normal');
  doc.setFontSize(PDF_TYPOGRAPHY.small);
  doc.setTextColor(...hexToRgb(PDF_COLORS.textMuted));
  doc.text(createdAt, PDF_PAGE.width - margins.right, 35, { align: 'right' });

  // Header line
  doc.setDrawColor(...hexToRgb(PDF_COLORS.primary));
  doc.setLineWidth(1.5);
  doc.line(margins.left, PDF_HEADER.lineY, PDF_PAGE.width - margins.right, PDF_HEADER.lineY);
}

/**
 * Adds footer with page number
 */
function addFooter(doc: jsPDF, currentPage: number, totalPages: number): void {
  const margins = PDF_PAGE.margins;

  // Footer line
  doc.setDrawColor(...hexToRgb(PDF_COLORS.border));
  doc.setLineWidth(0.5);
  doc.line(margins.left, PDF_FOOTER.lineY, PDF_PAGE.width - margins.right, PDF_FOOTER.lineY);

  // Page number
  doc.setFont('Montserrat', 'normal');
  doc.setFontSize(PDF_TYPOGRAPHY.caption);
  doc.setTextColor(...hexToRgb(PDF_COLORS.textMuted));
  doc.text(`Page ${currentPage} of ${totalPages}`, margins.left, PDF_FOOTER.textY);

  // Brand text - "Generated by" in muted, "GlitchAds" as link
  const prefix = 'Generated by ';
  const linkText = 'GlitchAds';
  const prefixWidth = doc.getTextWidth(prefix);
  const linkWidth = doc.getTextWidth(linkText);
  const totalWidth = prefixWidth + linkWidth;
  const startX = PDF_PAGE.width - margins.right - totalWidth;

  // "Generated by" in muted color
  doc.setTextColor(...hexToRgb(PDF_COLORS.textMuted));
  doc.text(prefix, startX, PDF_FOOTER.textY);

  // "GlitchAds" as link in primary color
  doc.setTextColor(...hexToRgb(PDF_COLORS.primary));
  doc.textWithLink(linkText, startX + prefixWidth, PDF_FOOTER.textY, {
    url: 'https://www.glitchads.ai',
  });
}

/**
 * Adds a new page with header (footer is added at the end when we know total pages)
 */
function addNewPage(doc: jsPDF, report: Report): void {
  doc.addPage();

  // Add header to new page (simplified - just line and page info)
  const margins = PDF_PAGE.margins;

  // Header line
  doc.setDrawColor(...hexToRgb(PDF_COLORS.primary));
  doc.setLineWidth(1.5);
  doc.line(margins.left, PDF_HEADER.lineY, PDF_PAGE.width - margins.right, PDF_HEADER.lineY);

  // Report type on left
  doc.setFont('Montserrat', 'normal');
  doc.setFontSize(PDF_TYPOGRAPHY.caption);
  doc.setTextColor(...hexToRgb(PDF_COLORS.textMuted));
  doc.text(REPORT_TYPE_LABELS[report.report_type] || 'Report', margins.left, 35);
}


/**
 * Renders text with inline links, handling word wrapping
 * Returns the new Y position after rendering
 */
function renderTextWithLinks(
  doc: jsPDF,
  text: string,
  startX: number,
  startY: number,
  maxWidth: number,
  lineHeight: number,
  onNewPage: () => number,
  setFont: () => void
): number {
  const segments = parseTextWithLinks(text);
  let currentX = startX;
  let currentY = startY;

  for (const segment of segments) {
    // Apply segment-level styling (italic/bold from outer markdown markers)
    if (segment.italic) {
      doc.setTextColor(...hexToRgb(PDF_COLORS.textMuted));
      doc.setFontSize(PDF_TYPOGRAPHY.small);
    }
    if (segment.bold) {
      doc.setFont('Montserrat', 'bold');
    }

    // Split segment text into words for wrapping
    const words = segment.text.split(/(\s+)/);

    for (const word of words) {
      if (!word) continue;

      const wordWidth = doc.getTextWidth(word);

      // Check if we need to wrap to next line
      if (currentX + wordWidth > startX + maxWidth && currentX > startX) {
        currentY += lineHeight;
        currentX = startX;

        // Check for page break
        if (currentY > PDF_FOOTER.lineY - 20) {
          currentY = onNewPage();
          setFont();
          // Re-apply segment styling after page break
          if (segment.italic) {
            doc.setTextColor(...hexToRgb(PDF_COLORS.textMuted));
            doc.setFontSize(PDF_TYPOGRAPHY.small);
          }
          if (segment.bold) {
            doc.setFont('Montserrat', 'bold');
          }
        }
      }

      // Render the word
      if (segment.url && word.trim()) {
        // Link text in primary color
        doc.setTextColor(...hexToRgb(PDF_COLORS.primary));
        doc.textWithLink(word, currentX, currentY, { url: segment.url });
        // Restore color based on segment style
        if (segment.italic) {
          doc.setTextColor(...hexToRgb(PDF_COLORS.textMuted));
        } else {
          doc.setTextColor(...hexToRgb(PDF_COLORS.text));
        }
      } else {
        doc.text(word, currentX, currentY);
      }

      currentX += wordWidth;
    }

    // Reset font after styled segment
    if (segment.italic || segment.bold) {
      setFont();
    }
  }

  return currentY;
}

/**
 * Renders a text block (markdown sections)
 */
/** 'report' is the branded report chrome; 'playbook' mirrors the strategy
 * report's screen rendering (#6671): no in-body H1 (the cover band carries
 * it), a rule under each section heading, grey boxes behind quotes. */
export type PdfTheme = 'report' | 'playbook';

async function renderTextBlock(
  doc: jsPDF,
  content: string,
  startY: number,
  onNewPage: () => number,
  theme: PdfTheme = 'report'
): Promise<number> {
  const sections = parseMarkdownSections(content);
  let currentY = startY;
  const margins = PDF_PAGE.margins;
  const maxWidth = PDF_PAGE.contentWidth;

  for (let sIdx = 0; sIdx < sections.length; sIdx++) {
    const section = sections[sIdx];
    const sectionHeight = estimateSectionHeight(section, maxWidth);

    // For headings, also require space for the next section to avoid orphans
    const isHeading = section.type === 'h1' || section.type === 'h2' || section.type === 'h3';
    let requiredHeight = sectionHeight;

    if (isHeading) {
      // Look ahead at all content until the next heading (or end) and require
      // space for the heading + that content block (capped at 120pt)
      let contentAfterHeading = 0;
      for (let j = sIdx + 1; j < sections.length; j++) {
        const nextType = sections[j].type;
        if (nextType === 'h1' || nextType === 'h2' || nextType === 'h3') break;
        contentAfterHeading += estimateSectionHeight(sections[j], maxWidth);
        if (contentAfterHeading >= 120) break;
      }
      requiredHeight = sectionHeight + Math.min(contentAfterHeading, 120);
    }

    if (currentY + requiredHeight > PDF_FOOTER.lineY - 20) {
      currentY = onNewPage();
    }

    switch (section.type) {
      case 'h1': {
        if (theme === 'playbook') break; // the cover band carries the title
        currentY += 30; // 30pt top margin for title
        doc.setFont('Montserrat', 'bold');
        doc.setFontSize(24); // 24pt title
        doc.setTextColor(...hexToRgb(PDF_COLORS.text));
        doc.setCharSpace(0);
        // Long titles (the playbook's "CAMPAIGN LAUNCH PLAYBOOK — {brand}")
        // must wrap within the content width, not run off the page.
        const h1Lines: string[] = doc.splitTextToSize(stripMarkdownFormatting(section.content), maxWidth);
        for (const line of h1Lines) {
          doc.text(line, margins.left, currentY);
          currentY += 24 * 1.15;
        }
        currentY -= 24 * 0.15; // font size only after the last line
        break;
      }

      case 'h2': {
        currentY += PDF_TYPOGRAPHY.sectionSpacing * 1.5;
        doc.setFont('Montserrat', 'bold');
        doc.setFontSize(PDF_TYPOGRAPHY.h2);
        doc.setTextColor(...hexToRgb(PDF_COLORS.text));
        doc.setCharSpace(0);
        const h2Lines: string[] = doc.splitTextToSize(stripMarkdownFormatting(section.content), maxWidth);
        for (const line of h2Lines) {
          doc.text(line, margins.left, currentY);
          currentY += PDF_TYPOGRAPHY.h2 * 1.15;
        }
        currentY += PDF_TYPOGRAPHY.headingSpacing - PDF_TYPOGRAPHY.h2 * 0.15;
        if (theme === 'playbook') {
          // The screen's rule under every section heading.
          doc.setDrawColor(...hexToRgb(PDF_COLORS.border));
          doc.setLineWidth(0.75);
          doc.line(margins.left, currentY - 6, margins.left + maxWidth, currentY - 6);
          currentY += 4;
        }
        break;
      }

      case 'h3': {
        currentY += PDF_TYPOGRAPHY.paragraphSpacing;
        doc.setFont('Montserrat', 'normal');
        doc.setFontSize(PDF_TYPOGRAPHY.h3);
        doc.setTextColor(...hexToRgb(PDF_COLORS.text));
        doc.setCharSpace(0);
        const h3Lines: string[] = doc.splitTextToSize(stripMarkdownFormatting(section.content), maxWidth);
        for (const line of h3Lines) {
          doc.text(line, margins.left, currentY);
          currentY += PDF_TYPOGRAPHY.h3 * 1.15;
        }
        currentY += PDF_TYPOGRAPHY.paragraphSpacing - PDF_TYPOGRAPHY.h3 * 0.15;
        break;
      }

      case 'paragraph': {
        const setParagraphFont = () => {
          doc.setFont('Montserrat', 'normal');
          doc.setFontSize(PDF_TYPOGRAPHY.body);
          doc.setTextColor(...hexToRgb(PDF_COLORS.text));
          doc.setCharSpace(0);
        };
        setParagraphFont();
        // Render text with links preserved
        currentY = renderTextWithLinks(
          doc,
          section.content,
          margins.left,
          currentY,
          maxWidth,
          PDF_TYPOGRAPHY.body * PDF_TYPOGRAPHY.lineHeight,
          onNewPage,
          setParagraphFont
        );
        currentY += PDF_TYPOGRAPHY.body * PDF_TYPOGRAPHY.lineHeight + PDF_TYPOGRAPHY.paragraphSpacing;
        break;
      }

      case 'list': {
        const setListFont = () => {
          doc.setFont('Montserrat', 'normal');
          doc.setFontSize(PDF_TYPOGRAPHY.body);
          doc.setTextColor(...hexToRgb(PDF_COLORS.text));
          doc.setCharSpace(0);
        };
        setListFont();

        // Use listItems if available (nested lists), fall back to items for compatibility
        const items: ListItem[] = section.listItems ||
          (section.items || []).map((text) => ({ text, depth: 0 }));

        const baseIndent = 15; // Base indent for bullets
        const depthIndent = 15; // Additional indent per nesting level

        for (const item of items) {
          if (currentY > PDF_FOOTER.lineY - 20) {
            currentY = onNewPage();
            setListFont();
          }

          const bulletX = margins.left + item.depth * depthIndent;
          const textX = bulletX + baseIndent;
          const availableWidth = maxWidth - (item.depth * depthIndent) - baseIndent;

          // Render bullet
          const bullet = '–';
          doc.text(bullet, bulletX, currentY);

          // Render text with links preserved
          currentY = renderTextWithLinks(
            doc,
            item.text,
            textX,
            currentY,
            availableWidth,
            PDF_TYPOGRAPHY.body * PDF_TYPOGRAPHY.lineHeight,
            onNewPage,
            setListFont
          );
          currentY += PDF_TYPOGRAPHY.body * PDF_TYPOGRAPHY.lineHeight;
        }
        currentY += PDF_TYPOGRAPHY.paragraphSpacing;
        break;
      }

      case 'hr':
        // Skip rendering — section spacing handled by layout
        currentY += 8;
        break;

      case 'table': {
        if (section.parsedTable && section.parsedTable.headers.length > 0) {
          const table = section.parsedTable;
          const colCount = table.headers.length;
          const tableWidth = maxWidth;
          const colWidth = tableWidth / colCount;
          const cellPadding = 8;
          const fontSize = colCount > 4 ? 9 : PDF_TYPOGRAPHY.body;
          const cellLineHeight = fontSize * PDF_TYPOGRAPHY.lineHeight;
          const cellContentWidth = colWidth - cellPadding * 2;

          // Helper: count actual wrapped lines using splitTextToSize
          const countCellLines = (text: string, bold: boolean = false): number => {
            doc.setFont('Montserrat', bold ? 'bold' : 'normal');
            doc.setFontSize(fontSize);
            const lines = doc.splitTextToSize(stripMarkdownFormatting(text), cellContentWidth);
            return Math.max(1, lines.length);
          };

          // Helper: calculate row height based on tallest cell
          const calcRowHeight = (cells: { text: string; bold?: boolean }[]): number => {
            let maxLines = 1;
            for (const cell of cells) {
              maxLines = Math.max(maxLines, countCellLines(cell.text, cell.bold));
            }
            return maxLines * cellLineHeight + cellPadding * 2;
          };

          // Helper: draw a table header row
          const drawHeaderRow = () => {
            const hdrHeight = calcRowHeight(table.headers);
            doc.setFillColor(249, 250, 251); // Gray-50 (matching /reports page)
            doc.rect(margins.left, currentY, tableWidth, hdrHeight, 'F');
            // Top border (drawn after fill so it's visible)
            doc.setDrawColor(...hexToRgb(PDF_COLORS.border));
            doc.setLineWidth(0.3);
            doc.line(margins.left, currentY, margins.left + tableWidth, currentY);
            doc.setFont('Montserrat', 'bold');
            doc.setFontSize(fontSize);
            doc.setTextColor(31, 41, 55); // Gray-800 text
            doc.setCharSpace(0);
            for (let c = 0; c < colCount; c++) {
              const cellText = stripMarkdownFormatting(table.headers[c].text);
              const cellCenterX = margins.left + c * colWidth + colWidth / 2;
              const cellY = currentY + cellPadding + fontSize;
              doc.text(cellText, cellCenterX, cellY, { align: 'center', maxWidth: cellContentWidth });
            }
            // Header borders
            doc.setDrawColor(...hexToRgb(PDF_COLORS.border));
            doc.setLineWidth(0.3);
            doc.line(margins.left, currentY + hdrHeight, margins.left + tableWidth, currentY + hdrHeight);
            // Vertical borders for header
            doc.line(margins.left, currentY, margins.left, currentY + hdrHeight);
            doc.line(margins.left + tableWidth, currentY, margins.left + tableWidth, currentY + hdrHeight);
            for (let c = 1; c < colCount; c++) {
              const borderX = margins.left + c * colWidth;
              doc.line(borderX, currentY, borderX, currentY + hdrHeight);
            }
            currentY += hdrHeight;
          };

          // Initial page break check (estimate min height: header + 2 rows)
          const minRowHeight = cellLineHeight + cellPadding * 2;
          if (currentY + minRowHeight * 3 > PDF_FOOTER.lineY - 20) {
            currentY = onNewPage();
          }

          // Draw header
          drawHeaderRow();

          // Draw data rows
          for (let r = 0; r < table.rows.length; r++) {
            const row = table.rows[r];
            const dynRowHeight = calcRowHeight(row);

            if (currentY + dynRowHeight > PDF_FOOTER.lineY - 20) {
              currentY = onNewPage();
              drawHeaderRow();
            }

            // White background
            doc.setFillColor(255, 255, 255);
            doc.rect(margins.left, currentY, tableWidth, dynRowHeight, 'F');

            // Cell text
            for (let c = 0; c < Math.min(row.length, colCount); c++) {
              const cell = row[c];
              doc.setFont('Montserrat', cell.bold ? 'bold' : 'normal');
              doc.setFontSize(fontSize);
              doc.setCharSpace(0);
              const cellCenterX = margins.left + c * colWidth + colWidth / 2;
              const cellY = currentY + cellPadding + fontSize;

              const trendArrow = parseTrendArrow(cell.text);
              if (c === 0) {
                // First column: left-aligned with word wrap
                const cellLeftX = margins.left + cellPadding;
                const textContent = trendArrow ? trendArrow.text : cell.text;
                if (trendArrow) {
                  doc.setTextColor(...hexToRgb(trendArrow.color));
                } else {
                  doc.setTextColor(...hexToRgb(PDF_COLORS.text));
                }
                const wrappedLines = doc.splitTextToSize(textContent, cellContentWidth);
                for (let line = 0; line < wrappedLines.length; line++) {
                  doc.text(wrappedLines[line], cellLeftX, cellY + line * cellLineHeight, { align: 'left' });
                }
              } else {
                // Other columns: centered, truncate to fit
                if (trendArrow) {
                  doc.setTextColor(...hexToRgb(trendArrow.color));
                  doc.text(trendArrow.text, cellCenterX, cellY, { align: 'center', maxWidth: cellContentWidth });
                } else {
                  doc.setTextColor(...hexToRgb(PDF_COLORS.text));
                  doc.text(cell.text, cellCenterX, cellY, { align: 'center', maxWidth: cellContentWidth });
                }
              }
            }

            // All borders drawn AFTER text to ensure they're not overridden
            doc.setDrawColor(...hexToRgb(PDF_COLORS.border));
            doc.setLineWidth(0.3);
            // Top border (for first row after header, this overlaps header bottom — that's fine)
            doc.line(margins.left, currentY, margins.left + tableWidth, currentY);
            // Bottom border
            doc.line(margins.left, currentY + dynRowHeight, margins.left + tableWidth, currentY + dynRowHeight);
            // Left border
            doc.line(margins.left, currentY, margins.left, currentY + dynRowHeight);
            // Right border
            doc.line(margins.left + tableWidth, currentY, margins.left + tableWidth, currentY + dynRowHeight);
            // Vertical cell dividers
            for (let c = 1; c < colCount; c++) {
              const borderX = margins.left + c * colWidth;
              doc.line(borderX, currentY, borderX, currentY + dynRowHeight);
            }

            currentY += dynRowHeight;
          }
          currentY += PDF_TYPOGRAPHY.sectionSpacing; // Extra spacing after table
        } else {
          // Fallback: render raw table lines as text
          const setTableFont = () => {
            doc.setFont('Montserrat', 'normal');
            doc.setFontSize(PDF_TYPOGRAPHY.body);
            doc.setTextColor(...hexToRgb(PDF_COLORS.text));
            doc.setCharSpace(0);
          };
          setTableFont();
          const tableLines = section.content.split('\n');
          for (const line of tableLines) {
            if (currentY > PDF_FOOTER.lineY - 20) {
              currentY = onNewPage();
              setTableFont();
            }
            doc.text(line, margins.left, currentY);
            currentY += PDF_TYPOGRAPHY.body * PDF_TYPOGRAPHY.lineHeight;
          }
        }
        currentY += PDF_TYPOGRAPHY.paragraphSpacing;
        break;
      }

      case 'blockquote': {
        const quoteIndent = 15;
        const borderX = margins.left;

        // Parse the blockquote content for nested lists/paragraphs
        const quoteContent = section.content;
        const quoteLines = quoteContent.split('\n').filter(l => l.trim());

        // Estimate height for page break
        const estimatedHeight = quoteLines.length * (PDF_TYPOGRAPHY.body * PDF_TYPOGRAPHY.lineHeight) + 16;
        if (currentY + estimatedHeight > PDF_FOOTER.lineY - 20) {
          currentY = onNewPage();
        }

        const quoteStartY = currentY;
        if (theme === 'playbook') {
          // The screen's grey box behind the quote (and the §1 hero block).
          // The fill must go down before the text, so measure the wrapped
          // height first with the same font and width the text will use.
          doc.setFont('Montserrat', 'normal');
          doc.setFontSize(PDF_TYPOGRAPHY.body);
          const lh = PDF_TYPOGRAPHY.body * PDF_TYPOGRAPHY.lineHeight;
          let boxHeight = 12 - lh;
          for (const qLine of quoteLines) {
            const plain = stripMarkdownFormatting(qLine.trim().replace(/^[-*+]\s/, ''));
            const wrapped: string[] = doc.splitTextToSize(plain, maxWidth - quoteIndent);
            boxHeight += Math.max(1, wrapped.length) * lh;
          }
          doc.setFillColor(249, 250, 251);
          doc.rect(borderX, quoteStartY, maxWidth, boxHeight, 'F');
        }
        currentY += 8; // Top padding

        const setQuoteFont = () => {
          doc.setFont('Montserrat', 'normal');
          doc.setFontSize(PDF_TYPOGRAPHY.body);
          doc.setTextColor(...hexToRgb(PDF_COLORS.textSecondary));
          doc.setCharSpace(0);
        };

        for (const qLine of quoteLines) {
          if (currentY > PDF_FOOTER.lineY - 20) {
            // Draw border for current page portion
            doc.setDrawColor(...hexToRgb(PDF_COLORS.primary));
            doc.setLineWidth(2.5);
            doc.line(borderX, quoteStartY, borderX, currentY);
            currentY = onNewPage();
          }

          const trimmed = qLine.trim();
          const qLineHeight = PDF_TYPOGRAPHY.body * PDF_TYPOGRAPHY.lineHeight;
          // Handle list items inside blockquotes
          if (/^[-*+]\s/.test(trimmed)) {
            const itemText = trimmed.slice(2).trim();
            setQuoteFont();
            doc.text('-', margins.left + quoteIndent, currentY);
            currentY = renderTextWithLinks(
              doc, itemText,
              margins.left + quoteIndent + 10, currentY,
              maxWidth - quoteIndent - 10,
              qLineHeight,
              onNewPage, setQuoteFont
            );
          } else if (trimmed) {
            setQuoteFont();
            currentY = renderTextWithLinks(
              doc, trimmed,
              margins.left + quoteIndent, currentY,
              maxWidth - quoteIndent,
              qLineHeight,
              onNewPage, setQuoteFont
            );
          }
          // Add spacing between blockquote lines (not after last)
          currentY += qLineHeight;
        }
        // Remove the extra line height added after the last item
        currentY -= PDF_TYPOGRAPHY.body * PDF_TYPOGRAPHY.lineHeight;
        // Small bottom padding
        currentY += 4;

        // Draw left border — extend to cover full text height
        doc.setDrawColor(...hexToRgb(PDF_COLORS.primary));
        doc.setLineWidth(2.5);
        doc.line(borderX, quoteStartY, borderX, currentY);

        currentY += PDF_TYPOGRAPHY.paragraphSpacing;
        break;
      }
    }
  }

  return currentY;
}

/**
 * Renders a scorecard block as a purple rounded rectangle with white text
 */
function renderScorecardBlock(
  doc: jsPDF,
  scorecard: ScorecardBlock,
  startY: number,
  onNewPage: () => number,
): number {
  const margins = PDF_PAGE.margins;
  const maxWidth = PDF_PAGE.width - margins.left - margins.right;
  const cx = margins.left + maxWidth / 2;

  let currentY = startY;

  const blockHeight = 25 + 20 + 12 + 16 + 24 + 32 + 40 + 15;

  if (currentY + blockHeight > PDF_FOOTER.lineY - 20) {
    currentY = onNewPage();
  }

  // Draw purple background first
  doc.setFillColor(...hexToRgb('#290BD9'));
  doc.roundedRect(margins.left, currentY, maxWidth, blockHeight, 4, 4, 'F');

  // All text is white from here
  let yPos = currentY + 25;

  // Section title
  doc.setFont('Montserrat', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.setCharSpace(0.5);
  doc.text(scorecard.sectionTitle.toUpperCase(), cx, yPos, { align: 'center' });
  doc.setCharSpace(0);
  yPos += 20;

  // Summary text
  doc.setFont('Montserrat', 'normal');
  doc.setFontSize(11);
  const summaryText = scorecard.summaryText.replace(/<[^>]+>/g, '');
  const summaryLines = doc.splitTextToSize(summaryText, maxWidth - 20);
  for (const line of summaryLines) {
    doc.text(line, cx, yPos, { align: 'center' });
    yPos += 12;
  }
  yPos += 16;

  // Divider (white, subtle)
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.3);
  doc.setGState(new (doc as any).GState({ opacity: 0.3 }));
  doc.line(margins.left + 15, yPos, margins.left + maxWidth - 15, yPos);
  doc.setGState(new (doc as any).GState({ opacity: 1 }));
  yPos += 24;

  // Subtitle
  doc.setFont('Montserrat', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.setCharSpace(0.5);
  doc.text(scorecard.subtitle.toUpperCase(), cx, yPos, { align: 'center' });
  doc.setCharSpace(0);
  yPos += 32;

  // Hero values
  const colWidth = maxWidth / 4;
  const values = scorecard.values.slice(0, 4);

  for (let i = 0; i < values.length; i++) {
    const colCx = margins.left + colWidth * i + colWidth / 2;

    doc.setFont('Montserrat', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    doc.text(values[i].value, colCx, yPos, { align: 'center' });

    doc.setFont('Montserrat', 'normal');
    doc.setFontSize(7);
    doc.setGState(new (doc as any).GState({ opacity: 0.75 }));
    doc.text(values[i].label, colCx, yPos + 16, { align: 'center' });
    doc.setGState(new (doc as any).GState({ opacity: 1 }));
  }

  currentY += blockHeight + PDF_TYPOGRAPHY.sectionSpacing;

  return currentY;
}

/**
 * Renders a chart block as SVG
 */
async function renderChartBlock(
  doc: jsPDF,
  svgString: string,
  startY: number,
  onNewPage: () => number
): Promise<number> {
  const margins = PDF_PAGE.margins;

  // Parse SVG and get dimensions
  const svgElement = parseSVGString(svgString);
  const dims = getSVGDimensions(svgElement);
  const scaled = scaleSVGToFit(dims.width, dims.height, PDF_CHARTS.maxWidth, PDF_CHARTS.maxHeight);

  // Check if chart fits on current page
  const chartHeight = scaled.height + PDF_CHARTS.marginTop + PDF_CHARTS.marginBottom;
  let currentY = startY + PDF_CHARTS.marginTop;

  if (currentY + chartHeight > PDF_FOOTER.lineY - 20) {
    currentY = onNewPage() + PDF_CHARTS.marginTop;
  }

  // Left-align chart with page content
  const chartX = margins.left;

  try {
    await svg2pdf(svgElement, doc, {
      x: chartX,
      y: currentY,
      width: scaled.width,
      height: scaled.height,
    });
  } catch (error) {
    // If SVG rendering fails, show placeholder
    doc.setFont('Montserrat', 'italic');
    doc.setFontSize(PDF_TYPOGRAPHY.small);
    doc.setTextColor(...hexToRgb(PDF_COLORS.textMuted));
    doc.text('[Chart could not be rendered]', chartX, currentY + 20);
  }

  return currentY + scaled.height + PDF_CHARTS.marginBottom;
}

/**
 * Estimates the height of a section for page break calculation
 */
function estimateSectionHeight(section: MarkdownSection, maxWidth: number): number {
  const lineHeight = PDF_TYPOGRAPHY.body * PDF_TYPOGRAPHY.lineHeight;

  switch (section.type) {
    case 'h1':
      return PDF_TYPOGRAPHY.h1 + PDF_TYPOGRAPHY.headingSpacing + PDF_TYPOGRAPHY.sectionSpacing;
    case 'h2':
      return PDF_TYPOGRAPHY.h2 + PDF_TYPOGRAPHY.headingSpacing + PDF_TYPOGRAPHY.sectionSpacing;
    case 'h3':
      return PDF_TYPOGRAPHY.h3 + PDF_TYPOGRAPHY.paragraphSpacing * 2;
    case 'paragraph': {
      // Rough estimate: 80 chars per line at body size
      const charPerLine = Math.floor(maxWidth / 5.5);
      const lines = Math.ceil(section.content.length / charPerLine);
      return lines * lineHeight + PDF_TYPOGRAPHY.paragraphSpacing;
    }
    case 'list':
      return (section.items?.length || 0) * lineHeight + PDF_TYPOGRAPHY.paragraphSpacing;
    case 'hr':
      return 25;
    case 'table': {
      const rowCount = section.parsedTable
        ? 1 + section.parsedTable.rows.length
        : section.content.split('\n').length;
      const rowH = PDF_TYPOGRAPHY.body * PDF_TYPOGRAPHY.lineHeight + 16; // cellPadding(8) * 2
      return rowCount * rowH + PDF_TYPOGRAPHY.paragraphSpacing;
    }
    case 'blockquote': {
      const qLines = section.content.split('\n').filter(l => l.trim()).length;
      return qLines * (PDF_TYPOGRAPHY.body * PDF_TYPOGRAPHY.lineHeight) + 24; // padding
    }
    default:
      return 20;
  }
}

/**
 * Generates filename for the PDF
 */
export function generatePDFFilename(report: Report): string {
  const start = new Date(report.report_period_start + 'T12:00:00');
  const end = new Date(report.report_period_end + 'T12:00:00');

  const formatDate = (d: Date) => {
    const month = d.toLocaleDateString('en-US', { month: 'short' });
    return `${month}_${d.getDate()}`;
  };

  const typeMap: Record<string, string> = {
    WEEKLY_PERFORMANCE: 'Weekly',
    MONTHLY_SUMMARY: 'Monthly',
    CUSTOM_SHORT: 'Custom',
    CUSTOM_LONG: 'Custom',
    STRATEGIC_6MONTH: 'Strategic',
    INTELLIGENCE: 'Intelligence',
    COMPETITOR_REPORT: 'Competitor',
    QUARTERLY_REVIEW: 'Quarterly',
  };
  const typeLabel = typeMap[report.report_type] || '';

  if (report.report_type === 'MONTHLY_SUMMARY') {
    const monthYear = start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).replace(/ /g, '_');
    return `GlitchAds_${typeLabel}_Performance_Report_${monthYear}.pdf`;
  }

  return `GlitchAds_${typeLabel}_Performance_Report_${formatDate(start)}-${formatDate(end)}_${end.getFullYear()}.pdf`;
}
